import { fetchEndpointData } from '@utils/fetchEndpointData';

import type {
  OrderChatMessage,
  OrderChatMessagesResponse,
  OrderChatSendResponse,
} from '../order/endpoints/chat/OrderChatEndpoint.types';
import type {
  CarrierPortalLinkCmrBody,
  CarrierPortalLinkCmrResponse,
} from './endpoints/orders/CarrierPortalLinkCmrEndpoint.types';
import type { CarrierPortalOrderType } from './endpoints/orders/CarrierPortalOrdersEndpoint.types';
import type { CarrierPortalTruckSimpleType as CarrierPortalTruckType } from './endpoints/trucks/CarrierPortalTrucksEndpoint.types';
import type { CarrierPortalOrderResponse as CarrierPortalOrderDetailType } from './endpoints/orders/CarrierPortalOrderEndpoint.types';
import type { CarrierPortalCarrierInfoResponse as CarrierPortalCarrierInfoType } from './endpoints/carrier/CarrierPortalCarrierInfoEndpoint.types';
import type { CarrierPortalKnowledgeBaseDocumentType } from './endpoints/misc/CarrierPortalKnowledgeBaseEndpoint.types';
import type { CarrierPortalSelfBillType } from './endpoints/misc/CarrierPortalSelfBillsEndpoint.types';

export async function linkCarrierCmrDocument(carrierId: number, data: Omit<CarrierPortalLinkCmrBody, 'carrierId'>) {
  const response = await fetchEndpointData<CarrierPortalLinkCmrResponse>(`/api/v1/carrier-portal/orders/link-cmr`, {
    method: 'POST',
    body: { ...data, carrierId },
  });
  return response;
}

// User management types
export type CarrierUserRole = 'CarrierDispatcher' | 'CarrierDriver';

export interface CarrierPortalUser {
  id: string;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

export interface CarrierPortalInvitedUser {
  id: string;
  email: string;
  name: string | null;
  role: CarrierUserRole;
  createdAt: string;
}

export interface CarrierPortalUsersResponse {
  users: CarrierPortalUser[];
  invitedUsers: CarrierPortalInvitedUser[];
}

export async function getCarrierPortalUsers(carrierId: number): Promise<CarrierPortalUsersResponse> {
  return fetchEndpointData<CarrierPortalUsersResponse>(`/api/v1/carrier-portal/users?carrierId=${carrierId}`);
}

export async function inviteCarrierPortalUser(
  carrierId: number,
  email: string,
  role: CarrierUserRole = 'CarrierDispatcher',
  name?: string,
): Promise<{ success: boolean; inviteId?: string }> {
  return fetchEndpointData('/api/v1/carrier-portal/users/invite', {
    method: 'POST',
    body: { carrierId, email, role, ...(name ? { name } : {}) },
  });
}

export async function deleteCarrierPortalInvite(carrierId: number, inviteId: string): Promise<{ success: boolean }> {
  return fetchEndpointData(`/api/v1/carrier-portal/users/invite/${inviteId}?carrierId=${carrierId}`, {
    method: 'DELETE',
  });
}

export interface UpdateUserRoleResponse {
  success: boolean;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
}

export async function updateCarrierPortalUserRole(
  carrierId: number,
  userId: string,
  role: CarrierUserRole,
): Promise<UpdateUserRoleResponse> {
  return fetchEndpointData('/api/v1/carrier-portal/users/update-role', {
    method: 'POST',
    body: { carrierId, userId, role },
  });
}

export interface RenameUserResponse {
  success: boolean;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
}

export async function renameCarrierPortalUser(
  carrierId: number,
  userId: string,
  name: string,
): Promise<RenameUserResponse> {
  return fetchEndpointData('/api/v1/carrier-portal/users/rename', {
    method: 'POST',
    body: { carrierId, userId, name },
  });
}

// Orders types - extracted from Zod schema in CarrierPortalOrdersEndpoint
export type CarrierPortalOrder = CarrierPortalOrderType;

// Client-side filters use strings for dates (before Zod transformation)
export interface CarrierPortalOrdersFilters {
  orderNumber?: string;
  carrierRef?: string;
  startDate?: string;
  endDate?: string;
  supplierTruckId?: string;
  assigneeId?: string;
}

export async function getCarrierPortalOrders(
  carrierId: number,
  language: string,
  filters?: CarrierPortalOrdersFilters,
  cursor?: string,
  take?: number,
): Promise<CarrierPortalOrder[]> {
  const params = new URLSearchParams({ language, carrierId: carrierId.toString() });

  if (cursor) params.set('id', cursor);
  if (take) params.set('take', take.toString());
  if (filters?.orderNumber) params.set('orderNumber', filters.orderNumber);
  if (filters?.carrierRef) params.set('carrierRef', filters.carrierRef);
  if (filters?.startDate) params.set('startDate', filters.startDate);
  if (filters?.endDate) params.set('endDate', filters.endDate);
  if (filters?.supplierTruckId) params.set('supplierTruckId', filters.supplierTruckId);
  if (filters?.assigneeId) params.set('assigneeId', filters.assigneeId);

  return fetchEndpointData<CarrierPortalOrder[]>(`/api/v1/carrier-portal/orders?${params.toString()}`);
}

// Trucks types - extracted from Zod schema in CarrierPortalTrucksEndpoint
export type CarrierPortalTruck = CarrierPortalTruckType;

export async function getCarrierPortalTrucks(carrierId: number): Promise<CarrierPortalTruck[]> {
  return fetchEndpointData<CarrierPortalTruck[]>(`/api/v1/carrier-portal/trucks?carrierId=${carrierId}`);
}

// Confirm order. The carrier has to explicitly accept the general terms &
// conditions (incl. the self-billing procedure) first — the server rejects the
// request otherwise.
export async function confirmCarrierPortalOrder(carrierId: number, orderId: string): Promise<{ success: boolean }> {
  return fetchEndpointData('/api/v1/carrier-portal/orders/confirm', {
    method: 'POST',
    body: { carrierId, orderId, termsAccepted: true },
  });
}

// Single order detail - extracted from Zod schema in CarrierPortalOrderEndpoint
export type CarrierPortalOrderDetail = CarrierPortalOrderDetailType;

export async function getCarrierPortalOrder(
  carrierId: number,
  orderId: string,
  language: string,
): Promise<CarrierPortalOrderDetail> {
  const params = new URLSearchParams({ language, carrierId: carrierId.toString() });
  return fetchEndpointData<CarrierPortalOrderDetail>(`/api/v1/carrier-portal/orders/${orderId}?${params.toString()}`);
}

// Link/unlink order documents
export async function linkCarrierPortalOrderDocument(
  carrierId: number,
  orderId: string,
  documentId: string,
  type: string,
): Promise<{ success: boolean }> {
  return fetchEndpointData('/api/v1/carrier-portal/orders/link-document', {
    method: 'POST',
    body: { carrierId, orderId, documentId, type },
  });
}

export async function unlinkCarrierPortalOrderDocument(
  carrierId: number,
  orderId: string,
  documentId: string,
  type: string,
): Promise<{ success: boolean }> {
  return fetchEndpointData('/api/v1/carrier-portal/orders/unlink-document', {
    method: 'POST',
    body: { carrierId, orderId, documentId, type },
  });
}

// Carrier info - extracted from Zod schema in CarrierPortalCarrierInfoEndpoint
export type CarrierPortalCarrierInfo = CarrierPortalCarrierInfoType;

export async function getCarrierPortalCarrierInfo(carrierId: number): Promise<CarrierPortalCarrierInfo> {
  return fetchEndpointData<CarrierPortalCarrierInfo>(`/api/v1/carrier-portal/carrier-info?carrierId=${carrierId}`);
}

// Knowledge base documents - extracted from Zod schema in CarrierPortalKnowledgeBaseEndpoint
export type CarrierPortalKnowledgeBaseDocument = CarrierPortalKnowledgeBaseDocumentType;

export async function getCarrierPortalKnowledgeBase(carrierId: number): Promise<CarrierPortalKnowledgeBaseDocument[]> {
  return fetchEndpointData<CarrierPortalKnowledgeBaseDocument[]>(
    `/api/v1/carrier-portal/knowledge-base?carrierId=${carrierId}`,
  );
}

// Truck CRUD operations
export interface CreateTruckData {
  licensePlate: string;
  insuranceExpiresAt: string;
  examinationExpiresAt: string;
}

export async function createCarrierPortalTruck(
  carrierId: number,
  data: CreateTruckData,
): Promise<{ success: boolean; truck: { id: string } }> {
  return fetchEndpointData('/api/v1/carrier-portal/trucks', {
    method: 'POST',
    body: { carrierId, ...data },
  });
}

export interface UpdateTruckData {
  licensePlate?: string;
  insuranceExpiresAt?: string;
  examinationExpiresAt?: string;
}

export async function updateCarrierPortalTruck(
  carrierId: number,
  truckId: string,
  data: UpdateTruckData,
): Promise<{ success: boolean; truck: { id: string } }> {
  return fetchEndpointData(`/api/v1/carrier-portal/trucks/${truckId}`, {
    method: 'PATCH',
    body: { carrierId, ...data },
  });
}

export async function updateCarrierPortalTruckAppPassword(
  carrierId: number,
  truckId: string,
  password: string,
): Promise<{ success: boolean; hasAppPassword: boolean; suggestedLogin: string }> {
  return fetchEndpointData(`/api/v1/carrier-portal/trucks/${truckId}/app-password`, {
    method: 'PATCH',
    body: { carrierId, password },
  });
}

export async function linkCarrierPortalTruckDocument(
  carrierId: number,
  truckId: string,
  documentId: string,
  type: string,
): Promise<{ success: boolean; truck: { id: string } }> {
  return fetchEndpointData('/api/v1/carrier-portal/trucks/link-document', {
    method: 'POST',
    body: { carrierId, truckId, documentId, type },
  });
}

// Contact CRUD operations
export interface CreateContactData {
  name: string;
  email?: string;
  phone?: string;
  function?: string;
  language: string;
  shouldReceiveTransportOrders?: boolean;
  shouldReceiveSelfBillings?: boolean;
  internalNotes?: string;
}

export async function createCarrierPortalContact(
  carrierId: number,
  data: CreateContactData,
): Promise<{ success: boolean; contact: { id: number } }> {
  return fetchEndpointData('/api/v1/carrier-portal/contacts', {
    method: 'POST',
    body: { carrierId, ...data },
  });
}

export interface UpdateContactData {
  name?: string;
  email?: string;
  phone?: string;
  function?: string;
  language?: string;
  shouldReceiveTransportOrders?: boolean;
  shouldReceiveSelfBillings?: boolean;
}

export async function updateCarrierPortalContact(
  carrierId: number,
  contactId: number,
  data: UpdateContactData,
): Promise<{ success: boolean; contact: { id: number } }> {
  return fetchEndpointData(`/api/v1/carrier-portal/contacts/${contactId}`, {
    method: 'PATCH',
    body: { carrierId, ...data },
  });
}

export async function deleteCarrierPortalContact(carrierId: number, contactId: number): Promise<{ success: boolean }> {
  return fetchEndpointData(`/api/v1/carrier-portal/contacts/${contactId}?carrierId=${carrierId}`, {
    method: 'DELETE',
  });
}

// Carrier update
export interface UpdateCarrierData {
  street?: string;
  streetNumber?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  language?: string;
  truckAmount?: number;
  tautlinerAmount?: number;
  boxTrailerAmount?: number;
  openTrailerAmount?: number;
}

export async function updateCarrierPortalCarrier(
  carrierId: number,
  data: UpdateCarrierData,
): Promise<{ success: boolean; carrier: { id: number } }> {
  return fetchEndpointData('/api/v1/carrier-portal/carrier', {
    method: 'PATCH',
    body: { carrierId, ...data },
  });
}

// Link supplier document
export type SupplierDocumentType = 'CivilLiabilityInsurance' | 'CmrInsurance' | 'TransportInsurance';

export async function linkCarrierPortalSupplierDocument(
  carrierId: number,
  documentId: string,
  type: SupplierDocumentType,
): Promise<{ success: boolean }> {
  return fetchEndpointData('/api/v1/carrier-portal/link-document', {
    method: 'POST',
    body: { carrierId, documentId, type },
  });
}

// Self-bills
export type CarrierPortalSelfBill = CarrierPortalSelfBillType;

export interface CarrierPortalSelfBillsFilters {
  search?: string;
}

export async function getCarrierPortalSelfBills(
  carrierId: number,
  cursor?: string,
  take?: number,
  filters?: CarrierPortalSelfBillsFilters,
): Promise<CarrierPortalSelfBill[]> {
  const params = new URLSearchParams({ carrierId: carrierId.toString() });

  if (cursor) params.set('id', cursor);
  if (take) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);

  return fetchEndpointData<CarrierPortalSelfBill[]>(`/api/v1/carrier-portal/self-bills?${params.toString()}`);
}

// Account request
export interface CarrierPortalAccountRequestData {
  name: string;
  email: string;
  companyName: string;
  companyNumber: string;
}

export async function requestCarrierPortalAccount(
  data: CarrierPortalAccountRequestData,
): Promise<{ success: boolean }> {
  return fetchEndpointData('/api/v1/carrier-portal/account-request', {
    method: 'POST',
    body: data,
  });
}

// ============================================================================
// Order Chat (uses generic order chat endpoints)
// ============================================================================

export type { OrderChatMessage };

export async function getCarrierPortalOrderChatMessages(
  orderId: string,
  cursor?: string,
  take?: number,
): Promise<OrderChatMessagesResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  if (take) params.set('take', take.toString());

  const queryString = params.toString();
  const url = queryString ? `/api/v1/orders/${orderId}/chat?${queryString}` : `/api/v1/orders/${orderId}/chat`;

  return fetchEndpointData<OrderChatMessagesResponse>(url);
}

export async function sendCarrierPortalOrderChatMessage(
  orderId: string,
  content: string,
): Promise<OrderChatSendResponse> {
  return fetchEndpointData<OrderChatSendResponse>(`/api/v1/orders/${orderId}/chat`, {
    method: 'POST',
    body: { content },
  });
}
